<template>
  <div class="picker-editor">
    <hr-text-field
      :model-value="search"
      :label="searchLabel"
      :autofocus="autofocus"
      clearable
      dense
      hide-bottom-space
      @update:model-value="search = $event ?? ''"
    >
      <template #prepend>
        <hr-icon name="search" />
      </template>
    </hr-text-field>

    <!--
      Drawn from every row rather than the filtered ones, so searching never appears to drop a
      selection the user has already made.
    -->
    <div class="picker-editor__chips">
      <span
        v-if="selectedRows.length === 0"
        class="picker-editor__empty"
      >
        {{ emptySelectionLabel }}
      </span>
      <hr-input-chip
        v-for="row in selectedRows"
        :key="row.id"
        :label="rowLabel(row)"
        :remove-aria-label="i18n.t(
          'common.filter.buttonRemoveSelectionLabel', { name: rowLabel(row) },
        )"
        @remove="onRemoveSelection(row)"
      />
    </div>

    <hr-data-table
      v-model:selected="selectedRows"
      :rows="rows"
      :columns="columns"
      :loading="isLoading"
      :filter="search"
      :pagination="pagination"
      :rows-per-page-options="[5, 25, 100, 500, 0]"
      row-key="id"
      title=""
      selection="multiple"
      @row-click="onRowClick"
    />
  </div>
</template>

<script setup>
import { filterPropertyTypes } from 'boot/globals';
import { i18n } from 'boot/i18n';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import {
  HrDataTable, HrIcon, HrInputChip, HrTextField, notifyNegative,
} from 'src/design-system';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { units as useUnitsStore } from 'src/store/units';
import { users as useUsersStore } from 'src/store/users';

import shouldAutofocus from '../autofocus';

/**
 * Clients and users, which stay a table.
 *
 * Deliberately not reduced to a chip row or a plain list: the supporting columns are what tell two
 * similarly named records apart, and losing them would make the picker worse rather than more
 * consistent. A table is the right shape here, so it is the design system's table.
 */
defineOptions({ name: 'PickerEditor' });

const props = defineProps({
  /** The criterion being edited. */
  criterion: {
    type: Object,
    required: true,
  },
  /** The statement being built for it. */
  modelValue: {
    type: Object,
    required: true,
  },
  /** Overrides the clients from the store, for callers filtering a set of their own. */
  customClients: {
    type: Array,
    default: null,
  },
  /** Overrides the users from the store, for the same reason. */
  customUsers: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const authStore = useAuthStore();
const clientsStore = useClientsStore();
const unitsStore = useUnitsStore();
const usersStore = useUsersStore();

const { clients, isLoadingClients } = storeToRefs(clientsStore);
const { users } = storeToRefs(usersStore);
const { units } = storeToRefs(unitsStore);

const search = ref('');
const autofocus = shouldAutofocus();
const isLoadingUsers = ref(false);

const isUsers = computed(() => [
  filterPropertyTypes.User,
  filterPropertyTypes.ArrayContainsUser,
].includes(props.criterion.filterType));

const unitName = computed(() => {
  const lookup = new Map((units.value ?? []).map((unit) => [unit.id, unit.unitName]));
  return (unitIds) => lookup.get(unitIds?.[0]) ?? '';
});

const availableClients = computed(() => props.customClients ?? clients.value ?? []);
const availableUsers = computed(() => props.customUsers ?? users.value ?? []);

/* Staff are pickable as users, not as clients, and an archived client cannot be filtered for. */
const rows = computed(() => (isUsers.value
  ? availableUsers.value
  : availableClients.value.filter((client) => client.type !== 'staff' && client.status === 'active')));

const columns = computed(() => {
  const unitColumn = {
    name: 'units',
    label: i18n.t('settings.units.columnUnitLabel'),
    field: 'units',
    align: 'left',
    format: (value) => unitName.value(value),
  };

  const textColumn = (name, labelKey) => ({
    name,
    label: i18n.t(labelKey),
    field: name,
    align: 'left',
    sortable: true,
  });

  return isUsers.value
    ? [
      textColumn('name', 'users.modals.editUser.inputNameLabel'),
      textColumn('email', 'users.modals.editUser.inputEmailLabel'),
      unitColumn,
    ]
    : [
      textColumn('clientName', 'clients.columnsClientNameLabel'),
      unitColumn,
    ];
});

/*
 * A page rather than a scroller. The dialog scrolls its own content, so a table that also scrolls
 * puts two scrollbars side by side, and paging already bounds how much is rendered at once.
 */
const pagination = computed(() => ({
  sortBy: isUsers.value ? 'name' : 'clientName',
  rowsPerPage: 5,
}));

const isLoading = computed(() => (isUsers.value ? isLoadingUsers.value : isLoadingClients.value));

const selectedIds = computed(() => (Array.isArray(props.modelValue.filteredItems)
  ? props.modelValue.filteredItems
  : []));

/*
 * A selected id the table cannot show: an archived client, or one outside the records the caller
 * supplied. It has no row to select and no chip to remove, but it is still part of the filter the
 * user applied, so it is carried through rather than dropped the moment they touch anything else.
 */
const unshownIds = computed(() => selectedIds.value.filter(
  (id) => !rows.value.some((row) => row.id === id),
));

/*
 * The table selects whole rows while the statement stores ids, so the two are translated here
 * rather than storing records the filter would have to carry around and keep fresh.
 */
const selectedRows = computed({
  get: () => rows.value.filter((row) => selectedIds.value.includes(row.id)),
  set: (records) => emit('update:modelValue', {
    ...props.modelValue,
    filteredItems: [...records.map((record) => record.id), ...unshownIds.value],
  }),
});

const searchLabel = computed(() => (isUsers.value
  ? i18n.t('common.filter.inputSearchUsers')
  : i18n.t('common.filter.inputSearchClients')));

const emptySelectionLabel = computed(() => (isUsers.value
  ? i18n.t('common.filter.textNoUsersSelected')
  : i18n.t('common.filter.textNoClientsSelected')));

const rowLabel = (row) => (isUsers.value ? row.name : row.clientName) ?? '';

function onRemoveSelection(row) {
  selectedRows.value = selectedRows.value.filter((record) => record.id !== row.id);
}

function onRowClick(event, row) {
  selectedRows.value = selectedIds.value.includes(row.id)
    ? selectedRows.value.filter((record) => record.id !== row.id)
    : [...selectedRows.value, row];
}

/*
 * Said out loud, because a failed load otherwise clears the spinner and leaves an empty table: the
 * user cannot tell "nobody matches" from "the read failed and retrying would work".
 */
function reportLoadFailure(error) {
  console.error('Error loading the filter picker records:', error);
  notifyNegative(i18n.t('common.errors.databaseReadError'));
}

/*
 * Loaded on mount rather than when the dialog opens, because this editor only exists once such a
 * criterion is chosen and most sessions never reach one.
 *
 * A caller that supplied its own records has already decided what is pickable, so fetching the
 * store's copy would be a read for data this table will not show.
 */
onMounted(async () => {
  unitsStore.loadClinicUnits().catch(reportLoadFailure);

  if (isUsers.value) {
    if (props.customUsers !== null) {
      return;
    }

    isLoadingUsers.value = true;
    try {
      await usersStore.bindUsers(authStore.userDetails?.clinic);
    } catch (error) {
      reportLoadFailure(error);
    } finally {
      isLoadingUsers.value = false;
    }
    return;
  }

  if (props.customClients === null) {
    clientsStore.syncClients().catch(reportLoadFailure);
  }
});
</script>

<style lang="scss" scoped>
.picker-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/*
 * Holds a chip's height whether or not there is a chip in it, so selecting the first record does
 * not shove the table down the pane. Chips run off to the right rather than wrapping, for the same
 * reason: a second line would move the table just as far.
 */
.picker-editor__chips {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: var(--app-comp-chip-container-height-md);
  overflow-x: auto;
}

.picker-editor__chips > * {
  flex: none;
}

.picker-editor__empty {
  color: var(--app-on-surface-variant);
}
</style>
